import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { loadMyContractorAccess } from '../services/vimPay/contractorService.js';
import { baseCommitment, listMyProjectAssignments } from '../services/vimPay/projectService.js';
import '../styles/vim-pay-projects.css';

function money(value, currency = 'USD') {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number.isFinite(amount) ? amount : 0);
}

function human(value, fallback = 'Not set') {
  const text = String(value || '').replace(/_/g, ' ').trim();
  return text ? text.replace(/\b\w/g, (letter) => letter.toUpperCase()) : fallback;
}

function formatDate(value) {
  if (!value) return 'Not set';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function VimPayProjectsPage() {
  const { ready, user } = useSession();
  const navigate = useNavigate();
  const [access, setAccess] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!ready || !user?.id) return;
    let active = true;
    Promise.all([loadMyContractorAccess(user.id), listMyProjectAssignments()])
      .then(([workerAccess, assignments]) => {
        if (!active) return;
        setAccess(workerAccess);
        setResult(assignments);
      })
      .catch((err) => { if (active) setError(err?.message || 'Unable to load VIM Pay projects.'); });
    return () => { active = false; };
  }, [ready, user?.id]);

  if (ready && !user) return <Navigate to="/pay/login" replace />;

  if (error) {
    return <section className="vim-pay-card vim-pay-center-card"><span className="vim-pay-kicker">PROJECT ACCESS</span><h1>Unable to load projects.</h1><p>{error}</p><button className="vim-pay-secondary" type="button" onClick={() => navigate('/pay')}>Back to dashboard</button></section>;
  }

  if (!ready || !access || !result) {
    return <section className="vim-pay-card vim-pay-center-card"><span className="vim-pay-kicker">VIM PAY — PHASE 3</span><h1>Loading your projects…</h1></section>;
  }

  if (!access.authorized) return <Navigate to="/pay" replace />;

  if (!result.configured) {
    return <section className="vim-pay-card vim-pay-center-card"><span className="vim-pay-kicker">PHASE 3</span><h1>Project assignments are being activated.</h1><p>Your worker access remains active. The Phase 3 project schema is not available yet.</p><button className="vim-pay-secondary" type="button" onClick={() => navigate('/pay')}>Back to dashboard</button></section>;
  }

  const assignments = result.assignments || [];
  const activeCount = assignments.filter((item) => ['assigned', 'active'].includes(item.status)).length;
  const baseTotal = assignments
    .filter((item) => item.status !== 'cancelled')
    .reduce((sum, item) => sum + baseCommitment(item), 0);

  return (
    <section className="vim-pay-dashboard">
      <div className="vim-pay-dashboard-heading">
        <div>
          <span className="vim-pay-kicker">VIM PAY — PHASE 3</span>
          <h1>My projects & rates</h1>
          <p>Review VIM-approved project assignments, roles, base rates, and authorized units. Workers cannot edit project rates from this portal.</p>
        </div>
        <button className="vim-pay-secondary" type="button" onClick={() => navigate('/pay')}>Back to dashboard</button>
      </div>

      <div className="vim-pay-project-summary">
        <article className="vim-pay-card"><span className="vim-pay-kicker">PROJECT ASSIGNMENTS</span><strong>{assignments.length}</strong><p>All current and historical Phase 3 assignments visible to your account.</p></article>
        <article className="vim-pay-card"><span className="vim-pay-kicker">OPEN ASSIGNMENTS</span><strong>{activeCount}</strong><p>Assigned or active work records.</p></article>
        <article className="vim-pay-card"><span className="vim-pay-kicker">AUTHORIZED BASE COMP</span><strong>{money(baseTotal)}</strong><p>Base rates × authorized units only. Payroll fringes, taxes, overtime, reimbursements and deductions are not included.</p></article>
      </div>

      {!assignments.length ? (
        <div className="vim-pay-card vim-pay-project-empty"><span className="vim-pay-kicker">NO PROJECTS YET</span><h2>No project assignment has been issued.</h2><p>When VIM Media assigns you to a production, the project and approved rate will appear here.</p></div>
      ) : (
        <div className="vim-pay-project-list">
          {assignments.map((assignment) => {
            const project = assignment.project || {};
            const unionPending = assignment.rate_validation_status === 'pending_payroll_rules';
            return (
              <article className="vim-pay-card vim-pay-project-card" key={assignment.id}>
                <div className="vim-pay-project-card-head">
                  <div><span className="vim-pay-kicker">{project.code || human(project.production_type, 'Production')}</span><h2>{project.name || 'VIM Media Project'}</h2><p>{assignment.department ? `${assignment.department} • ` : ''}{assignment.role_name}</p></div>
                  <span className={`vim-pay-project-status ${assignment.status}`}>{human(assignment.status)}</span>
                </div>

                <div className="vim-pay-project-rate-grid">
                  <div><small>Approved base rate</small><strong>{money(assignment.rate_amount, assignment.currency)}/{human(assignment.rate_unit)}</strong></div>
                  <div><small>Authorized units</small><strong>{Number(assignment.authorized_units || 0).toLocaleString('en-US')}</strong></div>
                  <div><small>Authorized base compensation</small><strong>{money(baseCommitment(assignment), assignment.currency)}</strong></div>
                  <div><small>Work window</small><strong>{formatDate(assignment.work_start)} – {formatDate(assignment.work_end)}</strong></div>
                </div>

                {unionPending ? <div className="vim-pay-project-warning"><strong>Payroll rule validation pending.</strong> This assignment is flagged for later union/payroll rule validation when VIM imports the payroll/fringe schedule. The amount above is base compensation only.</div> : null}
                {assignment.fringe_profile_key ? <p className="vim-pay-project-footnote">Future fringe profile: <strong>{assignment.fringe_profile_key}</strong></p> : null}
              </article>
            );
          })}
        </div>
      )}

      <div className="vim-pay-card vim-pay-project-boundary"><span className="vim-pay-kicker">PHASE 3 BOUNDARY</span><h2>Rates are visible, not worker-editable.</h2><p>Project managers and VIM Finance control production assignments and approved base rates in the CMS. Actual time worked, payment requests, union fringe calculations, payroll withholding, and money movement remain later phases.</p></div>
    </section>
  );
}
